import {
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  TemplateRef,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog"; // Import MatDialogRef for dialog reference
import { BaseChartLogic } from "app/analytics/BaseChartLogic";
import {
  AnalyticsService,
  AppUsageChartData,
  AppUsageChartDataPoint,
  AppUsageSession,
  AuthorizationService,
  ExportService,
  LoggingService,
  ManualOpenDataPoint,
  ToastrService,
} from "app/shared";
import { AppUsageConfig } from "app/shared/model/charts/user-analytic-configurations.model";
import { FormatTimePipe } from "app/shared/pipe/fortmatTime.pipe";

interface SessionExpandedState {
  [sessionId: string]: boolean;
}

interface ExpandedState {
  [userId: number]: {
    expanded: boolean;
    sessions: SessionExpandedState;
  };
}

interface ChartExpandedState {
  [permissionType: string]: {
    expanded: boolean;
    users: ExpandedState;
  };
}

@Component({
  selector: "app-usage-base-grid",
  templateUrl: "./app-usage-base-grid.component.html",
  styleUrls: ["./app-usage-base-grid.component.css"],
  standalone: false,
})
export class AppUsageBaseGridComponent
  extends BaseChartLogic<AppUsageConfig, AppUsageChartData>
  implements OnInit, OnDestroy
{
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() hasData: boolean = true;
  @Input() showTitle: boolean = true;

  protected listData: AppUsageChartDataPoint[] = [];
  protected isLoading: boolean = false;

  expandedState: ChartExpandedState = {};
  manualVisibilityMap = new Map<string, { [section: string]: boolean }>();

  // Modal management with MatDialog
  modalData: AppUsageSession | null = null;
  modalTitle: string = "";

  // Dialog Reference for the modal
  dialogRef: MatDialogRef<any> | null = null;

  // Modal template reference
  @ViewChild("modalTemplate") modalTemplate!: TemplateRef<any>;

  constructor(
    _analyticsService: AnalyticsService,
    _authService: AuthorizationService,
    _exportService: ExportService,
    _toastrService: ToastrService,
    _loggingService: LoggingService,
    _ngZone: NgZone,
    private dialog: MatDialog // Injecting MatDialog for modal functionality
  ) {
    super(
      _analyticsService,
      _authService,
      null,
      _toastrService,
      _loggingService,
      AppUsageConfig,
      _ngZone
    );

    this.handleDataSubscription(this.analyticsService.appUsageDataChange);

    this.analyticsService.appUsageDataChange.subscribe((data) => {
      this.listData = data.DataPoints.map((item) =>
        Object.assign(new AppUsageChartDataPoint(), item)
      );

      this.expandedState = {};
      this.listData.forEach((dataPoint) => {
        this.expandedState[dataPoint.TypeOfUser] = {
          expanded: false,
          users: {},
        };
      });

      this.hasData = true;

      this.isLoading = false;
    });
  }

  ngOnInit(): void {
    super.init();
  }

  ngOnDestroy(): void {
    super.cleanup();
  }

  userSearch: { [permissionType: string]: string } = {};

  filterUsers(permissionType: string, users: any[]): any[] {
    const searchTerm = this.userSearch[permissionType]?.toLowerCase() || "";
    return users.filter((user) =>
      `${user.FirstName} ${user.LastName} ${user.UserName}`
        .toLowerCase()
        .includes(searchTerm)
    );
  }

  dataPointKey: string = "Total";

  getDataPointDisplayValue(dataPoint: AppUsageChartDataPoint): any {
    return dataPoint.TypeOfUser;
  }

  buildFilterSettings(): void {
    this.addPeriodtoFilterSettings();
    this.addPermissionsToFilterSettings();
  }

  refreshData(): void {
    this.hasData = false;
    this.isLoading = true;
    this.addPeriodtoFilterSettings();
    this.addPermissionsToFilterSettings();
    this.analyticsService.getAppUsageData(this.filterSettings);
  }

  toggleDataPointExpand(permissionType: string): void {
    // Collapse all other panels
    for (const key in this.expandedState) {
      if (this.expandedState.hasOwnProperty(key)) {
        this.expandedState[key].expanded = false;
      }
    }

    // Toggle the selected one
    const currentState = this.expandedState[permissionType]?.expanded || false;
    this.expandedState[permissionType] = {
      expanded: !currentState,
      users: this.expandedState[permissionType]?.users || {},
    };
  }

  isDataPointExpanded(permissionType: string): boolean {
    return this.expandedState[permissionType]?.expanded || false;
  }

  toggleUserExpand(permissionType: string, userId: number): void {
    if (!this.expandedState[permissionType]) {
      this.expandedState[permissionType] = { expanded: false, users: {} };
    }

    const userState = this.expandedState[permissionType].users[userId] || {
      expanded: false,
      sessions: {},
    };

    userState.expanded = !userState.expanded;
    this.expandedState[permissionType].users[userId] = userState;
  }

  isUserExpanded(permissionType: string, userId: number): boolean {
    return (
      this.expandedState[permissionType]?.users?.[userId]?.expanded || false
    );
  }

  toggleSessionExpand(
    permissionType: string,
    userId: number,
    sessionId: string
  ): void {
    const sessionStates =
      this.expandedState[permissionType]?.users?.[userId]?.sessions || {};
    sessionStates[sessionId] = !sessionStates[sessionId];

    const users = this.expandedState[permissionType]?.users || {};
    if (!users[userId]) {
      users[userId] = { expanded: true, sessions: {} };
    }
    users[userId].sessions = sessionStates;
    this.expandedState[permissionType].users = users;
  }

  isSessionExpanded(
    permissionType: string,
    userId: number,
    sessionId: string
  ): boolean {
    return (
      this.expandedState[permissionType]?.users?.[userId]?.sessions?.[
        sessionId
      ] || false
    );
  }

  toggleVisibility(section: string, manual: ManualOpenDataPoint): void {
    const id = JSON.stringify(manual);
    if (!this.manualVisibilityMap.has(id)) {
      this.manualVisibilityMap.set(id, {});
    }
    const state = this.manualVisibilityMap.get(id)!;
    state[section] = !state[section];
  }

  isVisible(section: string, manual: ManualOpenDataPoint): boolean {
    const id = JSON.stringify(manual);
    return this.manualVisibilityMap.get(id)?.[section] || false;
  }

  getPagesViewed(pagesViewed): any[] {
    return Object.keys(pagesViewed).map((key) => ({
      pageNumber: key,
      count: pagesViewed[key],
    }));
  }

  getSearchTermsUsed(searchTermsUsed): any[] {
    return Object.keys(searchTermsUsed).map((key) => ({
      term: key,
      count: searchTermsUsed[key],
    }));
  }

  hasSearchTerms(manual: any): boolean {
    return Object.keys(manual.SearchTermsUsed || {}).length > 0;
  }

  hasPagesViewed(manual: any): boolean {
    return Object.keys(manual.PagesViewed || {}).length > 0;
  }

  // Open modal with session details
  openModal(session: AppUsageSession): void {
    this.modalData = session;
    this.modalTitle = "Session Details";

    // Open the dialog using MatDialog and passing modal data
    setTimeout(() => {
      this.dialogRef = this.dialog.open(this.modalTemplate, {
        width: "60%",
      });
    });
  }

  // Close modal
  closeModal(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
}
